import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, PackageSearch, ShieldAlert, CalendarRange, AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "../lib/api.js";
import StatusBadge from "./StatusBadge.jsx";
import { fmtRelative } from "../lib/format.js";

const COLOR_DOT = {
  red: "bg-[#DC3545]",
  yellow: "bg-[#FFC107]",
  green: "bg-[#28A745]",
  grey: "bg-[#94A3B8]",
};

const COLOR_BG = {
  red: "bg-red-50 border-red-200 text-red-800",
  yellow: "bg-yellow-50 border-yellow-200 text-yellow-800",
  green: "bg-green-50 border-green-200 text-green-800",
  grey: "bg-gray-50 border-gray-200 text-gray-500",
};

const FEEDBACK_DOT = {
  green: "bg-[#28A745]",
  yellow: "bg-[#FFC107]",
  red: "bg-[#DC3545]",
};

function Chip({ icon, label, count, color, status, testid }) {
  const disabled = count === 0;
  const cls = disabled ? COLOR_BG.grey : COLOR_BG[color] || COLOR_BG.grey;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 border rounded-sm text-xs ${cls} ${disabled ? "opacity-60" : ""}`}
      title={status ? `${label}: ${status}` : `${label}: sem registos`}
      data-testid={testid}
    >
      {icon}
      <span className="font-semibold tracking-wide">{label}</span>
      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${disabled ? "bg-white text-gray-400" : "bg-white text-[#1A1A2E]"}`} data-testid={`${testid}-count`}>
        {count}
      </span>
      {!disabled && status && (
        <span className={`w-1.5 h-1.5 rounded-full ${COLOR_DOT[color]}`} aria-hidden="true" />
      )}
    </div>
  );
}

function MiniWorkflowCard({ c }) {
  const overdue = c.escalation_level > 0;
  const borderColor = c.escalation_level >= 3 ? "border-l-[#DC3545]" : c.escalation_level >= 1 ? "border-l-[#FFC107]" : "border-l-[#1F3864]";
  return (
    <Link
      to={`/service-cases/${c.id}`}
      className={`block bg-white border border-gray-200 border-l-4 ${borderColor} rounded-md p-3 hover:shadow-md hover:border-[#1F3864] transition`}
      data-testid={`mycase-card-${c.number}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-base text-[#1F3864]" data-testid={`mycase-number-${c.number}`}>
              {c.number}
            </span>
            <StatusBadge status={c.current_status} />
            {c.feedback_status && (
              <span
                className={`w-2 h-2 rounded-full ${FEEDBACK_DOT[c.feedback_status] || "bg-gray-300"}`}
                title={`Feedback: ${c.feedback_status}`}
                data-testid={`mycase-feedback-${c.number}`}
              />
            )}
            {overdue && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#DC3545]"
                data-testid={`mycase-escalation-${c.number}`}
              >
                <AlertTriangle className="w-3 h-3" />
                L{c.escalation_level}
              </span>
            )}
          </div>
          <div className="text-sm text-[#1A1A2E] mt-1 truncate">
            {c.customer_name} · <span className="font-mono">{c.vehicle_registration}</span>
            <span className="text-[#6C757D]"> · {c.brand}{c.model ? ` ${c.model}` : ""}</span>
          </div>
          {c.next_action_deadline && (
            <div className="text-[11px] text-[#6C757D] mt-0.5">
              Próxima acção · {fmtRelative(c.next_action_deadline)}
            </div>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        <Chip
          icon={<ClipboardList className="w-3 h-3" />}
          label="PCP"
          count={c.chips.pcp.count}
          color={c.chips.pcp.color}
          status={c.chips.pcp.status}
          testid={`mycase-chip-pcp-${c.number}`}
        />
        <Chip
          icon={<PackageSearch className="w-3 h-3" />}
          label="PEC"
          count={c.chips.pec.count}
          color={c.chips.pec.color}
          status={c.chips.pec.status}
          testid={`mycase-chip-pec-${c.number}`}
        />
        <Chip
          icon={<ShieldAlert className="w-3 h-3" />}
          label="SWC"
          count={c.chips.swc.count}
          color={c.chips.swc.color}
          status={c.chips.swc.status}
          testid={`mycase-chip-swc-${c.number}`}
        />
        <Chip
          icon={<CalendarRange className="w-3 h-3" />}
          label="MPM"
          count={c.chips.mpm.count}
          color={c.chips.mpm.color}
          status={c.chips.mpm.status}
          testid={`mycase-chip-mpm-${c.number}`}
        />
      </div>
    </Link>
  );
}

export default function MyCasesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/dashboard/my-cases");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao carregar as obras.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="p-6 text-sm text-[#6C757D]" data-testid="mycases-loading">A carregar as suas obras…</div>;
  }
  if (error) {
    return <div className="p-6 text-sm text-[#DC3545]" data-testid="mycases-error">{error}</div>;
  }
  const cases = data?.cases || [];

  return (
    <div data-testid="mycases-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-[#6C757D]" data-testid="mycases-total">
          {cases.length === 0 ? "Sem obras abertas." : `${cases.length} obra${cases.length === 1 ? "" : "s"} aberta${cases.length === 1 ? "" : "s"}`}
          <span className="text-[#94A3B8]"> · ordenadas por urgência</span>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-sm hover:border-[#1F3864] hover:text-[#1F3864] text-xs"
          data-testid="mycases-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Actualizar
        </button>
      </div>

      {cases.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-gray-300 rounded-md text-sm text-[#6C757D]" data-testid="mycases-empty">
          Não tem obras abertas atribuídas. Bom trabalho!
        </div>
      ) : (
        <div className="space-y-2" data-testid="mycases-list">
          {cases.map((c) => (
            <MiniWorkflowCard key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}
